import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";

import { ShortcutService } from "@mapmycustomers/shared";
import Feature from "@mapmycustomers/shared/enum/Feature";
import { EntityType } from "@mapmycustomers/shared/types/entity";

import HideChat from "@app/component/chat/HideChat";
import Path from "@app/enum/Path";
import GroupsMode from "@app/scene/map/components/GroupsMode";
import LeadFinderMode from "@app/scene/map/components/LeadFinderMode";
import MapMode from "@app/scene/map/components/MapMode";
import MapPageMap from "@app/scene/map/components/MapPageMap";
import TerritoriesMode from "@app/scene/map/components/TerritoriesMode";
import mapAnalytics from "@app/scene/map/utils/mapAnalytics";
import MapSidebarContext from "@app/scene/map/utils/MapSidebarContext";
import { setEntityTypesWithExternalPreview } from "@app/store/entityView/actions";
import { doesOrganizationSupportTerritories } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useFeatures from "@app/util/hook/useFeatures";

import { setLocationSearchVisibility } from "./store/actions";

interface Props {
  onSetLocationSearchVisibility: typeof setLocationSearchVisibility;
  setEntityTypesWithExternalPreview: typeof setEntityTypesWithExternalPreview;
  territoriesEnabled: boolean;
}

const MapPage: React.FC<Props> = ({
  onSetLocationSearchVisibility,
  setEntityTypesWithExternalPreview,
  territoriesEnabled,
}) => {
  const { path } = useRouteMatch();

  const sidebarIds = useRef<string[]>([]);
  const [sidebarsWidths, setSidebarsWidths] = useState<Record<string, number>>({});
  const setSidebarWidth = useCallback((width: number, sidebarId: string) => {
    // replace most recent sidebar's width
    setSidebarsWidths((widths) => {
      const newWidths = { ...widths };
      if (width > 0) {
        newWidths[sidebarId] = width;
        if (!sidebarIds.current.includes(sidebarId)) {
          sidebarIds.current.push(sidebarId);
        }
      } else {
        delete newWidths[sidebarId];
        sidebarIds.current = sidebarIds.current.filter((id) => id !== sidebarId);
      }
      return newWidths;
    });
  }, []);
  const sidebarContext = useMemo(
    () => ({
      setSidebarWidth,
    }),
    [setSidebarWidth]
  );
  // taking width of the most recent non-zero sidebar
  const currentSidebarWidth = sidebarIds.current.reduceRight(
    (result, id) => ((sidebarsWidths[id] ?? 0) > result ? sidebarsWidths[id] : result),
    0
  );

  // enable external view to show own entity preview on map page
  useEffect(() => {
    setEntityTypesWithExternalPreview([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL]);
    return () => {
      setEntityTypesWithExternalPreview([]);
    };
  }, [setEntityTypesWithExternalPreview]);

  useEffect(() => {
    const shortcutService = new ShortcutService(document.body);
    shortcutService.addKeyListener(
      { code: "KeyL", metaKey: true, shiftKey: true, type: "keydown" },
      () => onSetLocationSearchVisibility(true)
    );
    return () => shortcutService.destroy();
  }, [onSetLocationSearchVisibility]);

  const { hasFeature } = useFeatures();
  if (!hasFeature(Feature.MAP)) {
    return <Redirect to={Path.DASHBOARD} />;
  }

  return (
    <AnalyticsContext.Provider value={mapAnalytics}>
      <HideChat>
        <MapSidebarContext.Provider value={sidebarContext}>
          <MapPageMap
            style={{
              marginRight: currentSidebarWidth,
              width: `calc(100% - ${currentSidebarWidth}px)`,
            }}
          >
            <div style={{ height: "100%" }}>
              <Switch>
                <Route component={MapMode} exact path={`${path}`} />
                <Route
                  component={GroupsMode}
                  path={[
                    `${path}/groups/:entityType/:groupId/entity/:sidebarEntityType/:sidebarEntityId`,
                    `${path}/groups/:entityType/:groupId`,
                  ]}
                />
                {territoriesEnabled && (
                  <Route
                    component={TerritoriesMode}
                    // can be a territoryId or "create"
                    path={[
                      `${path}/territories/:territoryId/entity/:sidebarEntityType/:sidebarEntityId`,
                      `${path}/territories/:territoryId`,
                    ]}
                  />
                )}
                <Route
                  component={LeadFinderMode}
                  path={[
                    `${path}/leads/entity/:sidebarEntityType/:sidebarEntityId`,
                    `${path}/leads`,
                  ]}
                />
                <Route
                  component={MapMode}
                  path={`${path}/entity/:sidebarEntityType/:sidebarEntityId`}
                />
                <Redirect to={`${path}`} />
              </Switch>
            </div>
          </MapPageMap>
        </MapSidebarContext.Provider>
      </HideChat>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  territoriesEnabled: doesOrganizationSupportTerritories(state),
});

const mapDispatchToProps = {
  onSetLocationSearchVisibility: setLocationSearchVisibility,
  setEntityTypesWithExternalPreview,
};

export default connect(mapStateToProps, mapDispatchToProps)(MapPage);
